<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import PlayoutService from '@/services/PlayoutService'
import StreamService from '@/services/StreamService'
import Badge from '../Badge.vue'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue'
import IconReplace from '../icon/IconReplace.vue'
import IconClock from '../icon/IconClock.vue'
import IconArrowRight from '../icon/IconArrowRight.vue'
import IconLinkChain from '../icon/IconLinkChain.vue'
import IconAlertTriangleStroke from '../icon/IconAlertTriangleStroke.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import StreamThumb from '../ui/StreamThumb.vue'
import StreamCardBadges from '../ui/StreamCard/StreamCardBadges.vue'
import ToggleStreamModal from '../modals/ToggleStreamModal.vue'
import ConfirmModal from '../modals/ConfirmModal.vue'

const props = defineProps({
	playout: {
		type: Object,
		default: () => ({}),
	},
})

const store = useStore()
const router = useRouter()
const { notify } = useNotification()

const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

const playoutConfig = ref(null)
const configLoaded = ref(false)
const loadPlayoutConfig = async () => {
	if (!props.playout?._id) return
	try {
		playoutConfig.value = await PlayoutService.getPlayoutConfig(props.playout._id)
		store.dispatch('Playout/cacheConfig', { id: props.playout._id, data: playoutConfig.value })
	} catch (e) {
		playoutConfig.value = null
	} finally {
		configLoaded.value = true
	}
}

onMounted(async () => {
	subscribeMediaPulse(props.playout)
	await loadPlayoutConfig()
})
onUnmounted(() => unsubscribeMediaPulse())

const open = () => {
	router.push({ name: 'PlayoutViewV2', params: { tvId: props.playout._id } })
}

const stop = (ev) => { ev?.preventDefault?.(); ev?.stopPropagation?.() }

/* enable / disable */
const isEnabled = computed(() => !!props.playout?.enabled)
const onToggleClick = (ev) => {
	stop(ev)
	showModal('modalToggleStream' + props.playout._id)
}

/* delete */
const hasDestination = computed(() => !!props.playout?.destination)
const deleteTooltip = computed(() => {
	if (!hasDestination.value) return ''
	const linked = props.playout.destination
	return `This TV Playout can't be deleted because it's used as a source in ${linked}. Remove it as a source first.`
})
const onDeleteClick = (ev) => {
	stop(ev)
	if (hasDestination.value) return
	showModal('delete-playout-v2-' + props.playout._id)
}
const onDeleteConfirm = async () => {
	hideModal('delete-playout-v2-' + props.playout._id)
	try {
		await StreamService.deleteStream(props.playout._id)
		await store.dispatch('Streams/getStreams', true)
		notify({ group: 'success', text: 'TV Playout deleted' })
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Failed to delete TV Playout' })
	}
}

/* meta */
const playoutFps = computed(() => playoutConfig.value?.settings?.fps || props.playout?.config?.fps || 25)
const playoutResolution = computed(() => {
	const raw = playoutConfig.value?.settings?.resolution || props.playout?.config?.resolution || '1280x720'
	if (raw === 'fullhd') return '1920x1080'
	if (raw === 'hd') return '1280x720'
	return raw
})
// Friendly, scannable resolution label (e.g. 720p) with the exact size kept as a tooltip.
const resolutionLabel = computed(() => {
	const map = {
		'3840x2160': '4K',
		'2560x1440': '1440p',
		'1920x1080': '1080p',
		'1280x720': '720p',
		'854x480': '480p',
		'640x360': '360p',
	}
	return map[playoutResolution.value] || playoutResolution.value
})
const filesCount = computed(() => playoutConfig.value?.playlist?.length ?? 0)
const isEmptyPlaylist = computed(() => configLoaded.value && filesCount.value === 0)
// Total scheduled runtime across all playlist items (best-effort; skips items without a valid span).
const totalDurationMs = computed(() => {
	const items = playoutConfig.value?.playlist || []
	return items.reduce((sum, it) => {
		const span = (it?.endTime || 0) - (it?.startTime || 0)
		return sum + (span > 0 ? span : 0)
	}, 0)
})
const totalDurationLabel = computed(() => {
	const ms = totalDurationMs.value
	if (!ms) return ''
	const totalMin = Math.round(ms / 60000)
	if (totalMin < 1) return '<1m'
	const days = Math.floor(totalMin / 1440)
	const hours = Math.floor((totalMin % 1440) / 60)
	const mins = totalMin % 60
	if (days > 0) return `${days}d ${hours}h`
	if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
	return `${mins}m`
})
const playoutMode = computed(() => {
	const mode = playoutConfig.value?.mode || props.playout?.config?.mode
	return mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : ''
})
const detailsLine = computed(() => {
	const parts = [resolutionLabel.value, `${playoutFps.value} fps`]
	if (filesCount.value > 0) {
		parts.push(`${filesCount.value} ${filesCount.value === 1 ? 'clip' : 'clips'}`)
		if (totalDurationLabel.value) parts.push(totalDurationLabel.value)
	}
	return parts.join(' • ')
})
const destinationName = computed(() => props.playout?.destination || '')
const hasDestinationName = computed(() => !!destinationName.value)

const playoutModeIcon = computed(() => {
	const mode = playoutConfig.value?.mode || props.playout?.config?.mode
	switch (mode) {
		case 'loop': return IconReplace
		case 'schedule': return IconClock
		default: return IconReplace
	}
})
</script>

<template>
	<figure class="flex flex-col w-full bg-surface-2 h-full relative rounded-xl group shadow-[0_1px_3px_rgba(0,0,0,0.15),0_10px_28px_-8px_rgba(0,0,0,0.4)] transition-[box-shadow,transform] duration-200 ease-out hover:shadow-[0_2px_6px_rgba(0,0,0,0.18),0_20px_44px_-10px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 m-0">
		<!-- Status badges overlay (Live / Offline / Disabled) — same as stream cards -->
		<StreamCardBadges :stream="playout" :media-pulse="mediaPulse" :compact-mode="false" :hide-preview="false" />

		<!-- Thumbnail / live preview — fixed aspect-video, matching StreamCardView -->
		<div class="relative shrink-0 overflow-hidden rounded-t-xl cursor-pointer bg-gradient-to-b from-surface-darker to-black w-full aspect-video"
			@click="open">
			<StreamThumb class="w-full h-full object-cover" :stream="playout" :media-pulse="mediaPulse" :compact-mode="false" />
			<div class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
				<span class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-surface-3/90 rounded-full px-3.5 py-1.5 shadow-lg">
					Manage
					<icon-base fill="none" width="14" height="14" width-view-box="24" height-view-box="24">
						<icon-arrow-right />
					</icon-base>
				</span>
			</div>
		</div>

		<!-- Caption -->
		<figcaption class="flex flex-col gap-2 !p-4 flex-1 min-h-[132px] cursor-pointer" @click="open">
			<div class="flex items-center gap-2 min-w-0 w-full">
				<h3 class="truncate text-lg text-light-1 font-medium m-0 min-w-0 flex-1" :title="playout.name">{{ playout.name }}</h3>
				<Badge v-if="playoutMode" type="main" inline pill size="sm" class="shrink-0 gap-1">
					<icon-base width="12" height="12" :width-view-box="16" :height-view-box="16">
						<component :is="playoutModeIcon" />
					</icon-base>
					{{ playoutMode }}
				</Badge>
			</div>
			<p class="text-tiny truncate flex items-center gap-1.5 m-0"
				:class="hasDestinationName ? 'text-surface-8' : 'text-surface-6'"
				:title="hasDestinationName ? `This playout streams to “${destinationName}”.` : 'Link this playout to an output stream to broadcast it.'">
				<icon-base class="shrink-0" width="12" height="12" width-view-box="14" height-view-box="14">
					<icon-link-chain />
				</icon-base>
				<template v-if="hasDestinationName">
					<span class="shrink-0 text-surface-6">Linked to</span>
					<span class="truncate">{{ destinationName }}</span>
				</template>
				<span v-else class="truncate">No destination set</span>
			</p>

			<!-- Meta (left) + actions (right) — same footer rhythm as stream cards -->
			<div class="flex items-center shrink-0 gap-x-2 mt-auto">
				<p v-if="isEmptyPlaylist" class="flex-1 min-w-0 text-tiny text-gold truncate flex items-center gap-1.5 m-0"
					title="This playout has no clips yet — add content so it has something to broadcast.">
					<icon-base class="shrink-0" fill="none" width="13" height="13" width-view-box="24" height-view-box="24">
						<icon-alert-triangle-stroke />
					</icon-base>
					<span class="truncate">No clips added yet</span>
				</p>
				<p v-else class="flex-1 min-w-0 text-tiny text-surface-7 truncate m-0"
					:title="`${playoutResolution} • ${playoutFps} fps`">{{ detailsLine }}</p>

				<div class="flex items-center gap-2 shrink-0" @click.stop>
					<!-- Delete: hover-reveal on desktop (matches stream cards) -->
					<Tooltip v-if="hasDestination" :content="deleteTooltip"
						class="lg:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
						<FormButton type="danger" size="sm" isIcon disabled>
							<icon-base><icon-bin /></icon-base>
						</FormButton>
					</Tooltip>
					<FormButton v-else type="danger" size="sm" isIcon
						class="lg:opacity-0 group-hover:opacity-100 transition-opacity duration-200"
						@click="onDeleteClick">
						<icon-base><icon-bin /></icon-base>
					</FormButton>
					<FormButton :type="isEnabled ? 'secondary' : 'main'" size="sm" @click="onToggleClick">
						{{ isEnabled ? 'Disable' : 'Enable' }}
					</FormButton>
				</div>
			</div>
		</figcaption>

		<ToggleStreamModal :stream="playout" />
		<ConfirmModal :modalId="'delete-playout-v2-' + playout._id"
			message="Are you sure you want to delete your TV Playout?" modal-type="danger"
			@modal-confirm="onDeleteConfirm" />
	</figure>
</template>
